import {
  alpha,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  ImageList,
  ImageListItem,
  Link,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  AccessTime,
  AssignmentTurnedInOutlined,
  BakeryDining,
  BedOutlined,
  ChatBubbleOutline,
  Delete,
  Edit,
  EmojiFoodBeverage,
  FreeBreakfast,
  HourglassBottom,
  Layers,
  LunchDining,
  PhotoLibrary,
  RestaurantMenuOutlined,
  Spa,
  VisibilityOutlined,
} from "@mui/icons-material";
import NappyIcon from "components/utils/NappyIcon";
import { stringAvatar } from "utils";
import { isNurseryAdmin } from "utils/roles";
import { toAssetUrl } from "utils/apiConfig";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { IChild } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { AuthState } from "store/features/auth";

interface ILocalizedRow {
  id: number;
  code: string;
  labelEn: string;
  labelFr?: string;
  labelAr?: string;
}

export interface IEvent {
  id: string | number;
  child: IChild;
  createdAt: string;
  infos: { [props: string]: string };
  indicators: { [props: string]: string };
  poster: { id: number; firstName: string; lastName: string; photo?: string };
  eventType: IEventType;
  comments: Array<{ content: string }>;
  images: string[];
  area?: ILocalizedRow | null;
  ageBand?: ILocalizedRow | null;
}

const pickLabel = (
  row: ILocalizedRow | null | undefined,
  lang: string,
): string => {
  if (!row) return "";
  const short = lang.substring(0, 2).toLowerCase();
  return (
    (short === "fr" ? row.labelFr : short === "ar" ? row.labelAr : null) ??
    row.labelEn
  );
};

export interface IEventType {
  name: string;
  id: string;
  fields: string;
}

const EVENT_ICONS: Record<string, React.ReactElement> = {
  observation: <VisibilityOutlined sx={{ fontSize: 14 }} />,
  nappy: <NappyIcon />,
  sleep: <BedOutlined sx={{ fontSize: 14 }} />,
  meal: <RestaurantMenuOutlined sx={{ fontSize: 14 }} />,
  assessment: <AssignmentTurnedInOutlined sx={{ fontSize: 14 }} />,
  photos: <PhotoLibrary sx={{ fontSize: 14 }} />,
  menu: <RestaurantMenuOutlined sx={{ fontSize: 14 }} />,
};

const EVENT_COLORS: Record<
  string,
  "primary" | "success" | "warning" | "info" | "error" | "secondary"
> = {
  observation: "primary",
  nappy: "warning",
  sleep: "info",
  meal: "success",
  assessment: "secondary",
  photos: "error",
  menu: "success",
};

// Menu events render as a fixed-order coloured list (breakfast first). The
// order can't come from `infos` because Postgres jsonb reorders object keys.
const MENU_MEAL_ORDER = ["breakfast", "snack", "lunch", "tea_time"] as const;
const MENU_MEAL_META: Record<
  string,
  { icon: React.ReactElement; color: string }
> = {
  breakfast: {
    icon: <FreeBreakfast sx={{ fontSize: 18 }} />,
    color: "#F59E0B",
  },
  snack: { icon: <BakeryDining sx={{ fontSize: 18 }} />, color: "#EC4899" },
  lunch: { icon: <LunchDining sx={{ fontSize: 18 }} />, color: "#10B981" },
  tea_time: {
    icon: <EmojiFoodBeverage sx={{ fontSize: 18 }} />,
    color: "#8B5CF6",
  },
};

interface EventProps {
  item: IEvent;
  onCommentClick: (id: string) => void;
  onFrameIndicatorClick: (id: string) => void;
  onEdit?: (item: IEvent) => void;
  onDelete?: (item: IEvent) => void;
}

const Event: React.FC<EventProps> = ({
  item,
  onCommentClick,
  onFrameIndicatorClick,
  onEdit,
  onDelete,
}) => {
  const authInfo = useSelector<RootState, AuthState>((store) => store.auth);
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const {
    child: { firstName, lastName, photo },
    createdAt,
    infos,
    indicators,
    poster,
    images,
    area,
    ageBand,
  } = item;
  const cellLabel = [
    pickLabel(area, i18n.language),
    pickLabel(ageBand, i18n.language),
  ]
    .filter(Boolean)
    .join(" · ");
  const name = `${firstName} ${lastName}`;

  // `type` is a legacy classifier ("inline") on older Assessment events.
  // Strip it so it doesn't render as a "TYPE: inline" field on the card.
  // menuWeekId/dayOfWeek are Menu-post bookkeeping, not human-readable fields.
  const {
    from,
    to,
    duration,
    type: _type,
    menuWeekId: _menuWeekId,
    dayOfWeek: _dayOfWeek,
    ...otherInfos
  } = infos || {};
  const isAdmin = isNurseryAdmin(authInfo.role);
  const isOwnPost = poster?.id !== undefined && poster.id === authInfo.id;
  const canMutate = isAdmin || isOwnPost;
  const commentCount = (item.comments || []).length;
  // indicators may be an array (post-fix shape) or a keyed object
  // (legacy shape from before backend coerced to array).
  const indicatorCount = Array.isArray(indicators)
    ? indicators.length
    : indicators && typeof indicators === "object"
      ? Object.keys(indicators).length
      : 0;

  const formatDuration = (dur: string) => {
    const val = parseFloat(dur);
    if (isNaN(val)) return dur;
    const hours = Math.floor(val);
    const minutes = Math.round((val - hours) * 60);
    if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h`;
    return `${minutes}m`;
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        mb: 2,
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        {/* Header: avatar + name + date + type badge + actions */}
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <Avatar
            src={toAssetUrl(photo as string | undefined)}
            {...(!photo
              ? stringAvatar(name, { width: 44, height: 44, fontSize: 18 })
              : { sx: { width: 44, height: 44 } })}
          />
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight={600}>
              {name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dayjs(createdAt).format("DD MMM YYYY · HH:mm")}
            </Typography>
          </Box>
          <Chip
            icon={EVENT_ICONS[item.eventType.name.toLowerCase()]}
            label={t("posts." + item.eventType.name.toLowerCase())}
            size="small"
            color={EVENT_COLORS[item.eventType.name.toLowerCase()] || "primary"}
            sx={{ fontWeight: 600, fontSize: 11 }}
          />
          {/* Editing assessments is disabled — the dialog only exposes the
              note and the dev-page round-trip risks orphaning the indicator
              snapshot. Re-enable once the dialog can edit indicators safely. */}
          {canMutate && onEdit && item.eventType.name !== "Assessment" && (
            <Tooltip title={t("labels.edit")}>
              <IconButton size="small" onClick={() => onEdit(item)}>
                <Edit sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          {canMutate && onDelete && (
            <Tooltip title={t("buttons.delete")}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(item)}
              >
                <Delete sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Images */}
        {images && images.length > 0 && (
          <ImageList
            cols={images.length === 1 ? 1 : images.length === 2 ? 2 : 3}
            gap={6}
            sx={{ borderRadius: 2, overflow: "hidden", mb: 2, mt: 0 }}
          >
            {images.map((image) => (
              <ImageListItem key={image}>
                <img
                  src={toAssetUrl(image)}
                  alt={name}
                  loading="lazy"
                  style={{
                    objectFit: "cover",
                    height: images.length === 1 ? 280 : 160,
                    width: "100%",
                  }}
                />
              </ImageListItem>
            ))}
          </ImageList>
        )}

        {/* Time & duration (for Sleep events) */}
        {(from || to) && (
          <Box display="flex" gap={3} mb={1.5}>
            <Box display="flex" alignItems="center" gap={0.75}>
              <AccessTime sx={{ fontSize: 16, color: "text.disabled" }} />
              <Typography variant="body2">
                {from}
                {to ? ` — ${to}` : ""}
              </Typography>
            </Box>
            {duration && (
              <Box display="flex" alignItems="center" gap={0.75}>
                <HourglassBottom
                  sx={{ fontSize: 16, color: "text.disabled" }}
                />
                <Typography variant="body2">
                  {formatDuration(duration)}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Menu events: coloured meal list in a fixed order (breakfast first) */}
        {item.eventType?.name === "Menu" ? (
          <>
            <Box
              sx={{
                borderRadius: 2,
                overflow: "hidden",
                border: 1,
                borderColor: "divider",
                mb: 0.5,
              }}
            >
              {MENU_MEAL_ORDER.filter((k) => otherInfos[k]).map(
                (k, idx, arr) => {
                  const meta = MENU_MEAL_META[k];
                  return (
                    <Box
                      key={k}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      px={1.5}
                      py={1}
                      sx={{
                        borderBottom: idx < arr.length - 1 ? 1 : 0,
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 1.5,
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          color: meta.color,
                          bgcolor: alpha(meta.color, 0.12),
                        }}
                      >
                        {meta.icon}
                      </Box>
                      <Box minWidth={0}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={700}
                          sx={{
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                            display: "block",
                          }}
                        >
                          {t(k)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.4,
                            direction: "auto",
                            unicodeBidi: "plaintext",
                          }}
                        >
                          {otherInfos[k]}
                        </Typography>
                        {(infos as any)?.dietary?.[k] && (
                          <Typography
                            variant="caption"
                            sx={{
                              mt: 0.25,
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: "success.dark",
                              direction: "auto",
                              unicodeBidi: "plaintext",
                            }}
                          >
                            <Spa sx={{ fontSize: 13 }} />
                            {t("menu.option_dietary")}:{" "}
                            {(infos as any).dietary[k]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                },
              )}
            </Box>
            {((infos as any)?.vegetarian ||
              ((infos as any)?.allergens?.length ?? 0) > 0) && (
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={1} mb={0.5}>
                {(infos as any)?.vegetarian && (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={t("menu.vegetarian")}
                  />
                )}
                {((infos as any)?.allergens ?? []).map((a: string) => (
                  <Chip
                    key={a}
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={t(`menu.allergen_list.${a}`)}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          /* Info fields (notes, nappy type, meal type, quantity, etc.) */
          Object.keys(otherInfos).map((key) => {
            if (!otherInfos[key]) return null;
            return (
              <Box mb={1} key={key}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {t(`${key}`)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.6,
                    direction: "auto",
                    unicodeBidi: "plaintext",
                  }}
                >
                  {otherInfos[key]}
                </Typography>
              </Box>
            );
          })
        )}

        {/* Footer: poster + indicators + comments */}
        <Divider sx={{ my: 1.5 }} />
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              avatar={
                poster.photo ? (
                  <Avatar src={toAssetUrl(poster.photo)} />
                ) : (
                  <Avatar
                    {...stringAvatar(`${poster.firstName} ${poster.lastName}`, {
                      fontSize: 13,
                      color: "#fff !important",
                    })}
                  />
                )
              }
              label={`${poster.firstName} ${poster.lastName}`}
              variant="outlined"
              size="small"
              sx={{
                "& .MuiChip-avatar": { width: 22, height: 22, fontSize: 10 },
              }}
            />
            {indicatorCount > 0 && (
              <Chip
                icon={<Layers sx={{ fontSize: 14 }} />}
                label={t("labels.framework_indicator_count", {
                  count: indicatorCount,
                })}
                variant="outlined"
                size="small"
                onClick={() => onFrameIndicatorClick(item.id as string)}
                sx={{ cursor: "pointer" }}
              />
            )}
            {cellLabel && (
              <Chip
                label={cellLabel}
                variant="outlined"
                size="small"
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  borderColor: alpha(theme.palette.secondary.main, 0.4),
                  color: "text.secondary",
                }}
              />
            )}
          </Stack>

          <Chip
            icon={<ChatBubbleOutline sx={{ fontSize: 14 }} />}
            label={t("labels.comment_count", { count: commentCount })}
            variant={commentCount > 0 ? "filled" : "outlined"}
            color={commentCount > 0 ? "primary" : "default"}
            size="small"
            onClick={() => onCommentClick(item.id as string)}
            sx={{
              borderRadius: 1.5,
              cursor: "pointer",
              fontWeight: commentCount > 0 ? 600 : 400,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default Event;
